/**
 * Shared layout and text styles (Griffel) — the Fluent UI v9 replacement for
 * the legacy global SCSS utility classes.
 *
 * Spacing uses Fluent's `tokens.spacingHorizontal*` / `tokens.spacingVertical*`
 * — that is the spacing convention across the app; there is no custom spacing
 * scale. Border-radius, font-size, and font-weight all use Fluent `tokens.*`
 * directly too (no custom scales). Colors come from the single source,
 * styles/colors.ts (re-exported below). Use these hooks instead of legacy
 * `className="flex gap-md mb-lg"` strings:
 *
 *   const layout = useLayoutStyles();
 *   <div className={mergeClasses(layout.flex, layout.itemsCenter, layout.gapL)}>
 */

import { makeStyles, tokens } from '@fluentui/react-components';

// Brand colors are the single source in styles/colors.ts; import them
// directly from there (these hooks consume a few brand colors internally).
import { brand } from './colors';

// Re-export brand and shadow so components can do a single import from this module.
export { brand, shadow } from './colors';

export const useLayoutStyles = makeStyles({
  // ---- Page scaffolding ----
  // AppLayout's body owns the page max-width and the top/horizontal padding
  // (24px) so every page's header lands at the same spot; pageContainer only
  // adds breathing room at the bottom of the scroll area. Don't re-add
  // top/horizontal padding here — it double-stacks with AppLayout.
  pageContainer: {
    paddingBottom: tokens.spacingVerticalXXXL,
  },
  // No marginBottom: the header→content gap is owned by the page container's
  // flex row-gap (`gapL`). Adding a margin here double-stacks with that gap.
  pageHeader: {},
  pageHeaderSplit: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXXL,
    columnGap: tokens.spacingHorizontalXXL,
  },
  pageTitle: {
    marginBottom: tokens.spacingVerticalXS,
  },
  pageSubtitle: {
    fontSize: tokens.fontSizeBase300,
    color: brand.gray50,
    fontWeight: 300,
  },
  /** Action row under a page title. */
  pageActions: {
    display: 'flex',
    alignItems: 'stretch',
    rowGap: tokens.spacingVerticalL,
    columnGap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalXXL,
    // Equal control heights: stretch fills the row (tallest control wins);
    // Fluent field controls need height:100% since their roots fix their own
    // height and would otherwise ignore the stretch.
    '& .fui-Input, & .fui-Combobox, & .fui-Dropdown': { height: '100%' },
    '@media (max-width: 640px)': {
      flexDirection: 'column',
    },
  },
  /** Action row inside a split header — no top margin. */
  pageActionsInline: {
    display: 'flex',
    alignItems: 'stretch',
    rowGap: tokens.spacingVerticalL,
    columnGap: tokens.spacingHorizontalL,
    '& .fui-Input, & .fui-Combobox, & .fui-Dropdown': { height: '100%' },
  },
  section: {
    marginBottom: '3rem',
  },

  // ---- Grids ----
  grid2: {
    display: 'grid',
    rowGap: tokens.spacingVerticalXXL,
    columnGap: tokens.spacingHorizontalXXL,
    gridTemplateColumns: '1fr',
    '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  grid3: {
    display: 'grid',
    rowGap: tokens.spacingVerticalXXL,
    columnGap: tokens.spacingHorizontalXXL,
    gridTemplateColumns: '1fr',
    '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
  },
  grid4: {
    display: 'grid',
    rowGap: tokens.spacingVerticalXXL,
    columnGap: tokens.spacingHorizontalXXL,
    gridTemplateColumns: '1fr',
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(4, 1fr)' },
  },
  /** Card grid for catalog-style cards. */
  cardGrid: {
    display: 'grid',
    rowGap: tokens.spacingVerticalXXL,
    columnGap: tokens.spacingHorizontalXXL,
    gridTemplateColumns: '1fr',
    '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
  },

  // ---- Flex utilities ----
  flex: { display: 'flex' },
  flexCol: { display: 'flex', flexDirection: 'column' },
  flexWrap: { flexWrap: 'wrap' },
  itemsCenter: { alignItems: 'center' },
  itemsStart: { alignItems: 'flex-start' },
  itemsEnd: { alignItems: 'flex-end' },
  justifyCenter: { justifyContent: 'center' },
  justifyBetween: { justifyContent: 'space-between' },
  justifyEnd: { justifyContent: 'flex-end' },
  // Each gap key's letter maps to the Fluent spacing token of the same size.
  gapXS: { rowGap: tokens.spacingVerticalXS, columnGap: tokens.spacingHorizontalXS },
  gapS: { rowGap: tokens.spacingVerticalS, columnGap: tokens.spacingHorizontalS },
  gapL: { rowGap: tokens.spacingVerticalL, columnGap: tokens.spacingHorizontalL },
  gapXXL: { rowGap: tokens.spacingVerticalXXL, columnGap: tokens.spacingHorizontalXXL },

  // ---- Empty state ----
  emptyState: {
    paddingTop: '4rem',
    paddingBottom: '4rem',
    paddingLeft: tokens.spacingHorizontalXXXL,
    paddingRight: tokens.spacingHorizontalXXXL,
    textAlign: 'center',
  },
  emptyStateIcon: {
    marginBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground3,
  },
  emptyStateText: {
    fontSize: tokens.fontSizeBase300,
    color: brand.gray50,
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
  },

  // ---- Misc ----
  w100: { width: '100%' },
  /** Align a Fluent Checkbox's indicator with field/input left edges (the
   *  indicator carries an 8px internal margin). */
  checkboxFlush: { marginLeft: '-8px' },
  linkStyle: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    ':hover': { color: tokens.colorBrandForeground2 },
  },
  overflowXAuto: { overflowX: 'auto' },
  cursorPointer: { cursor: 'pointer' },
  fullHeight: { height: '100%' },

  /** Tinted table header row — the shared "look and feel" for list/table
   *  controls (apply to the <TableRow> inside <TableHeader>). Adds a slight
   *  separator under the header. */
  tableHeaderRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    '& th': {
      backgroundColor: tokens.colorNeutralBackground2,
      borderBottomWidth: tokens.strokeWidthThin,
      borderBottomStyle: 'solid',
      borderBottomColor: tokens.colorNeutralStroke1,
    },
  },
  // Responsive table/card toggle — switch at 800px (the table's minWidth).
  // Uses a container query on .table-card-container so the breakpoint tracks
  // the actual available width, not the viewport.
  tableOnlyView: { '@container (max-width: 800px)': { display: 'none' } },
  cardOnlyView: { display: 'none', '@container (max-width: 800px)': { display: 'block' } },
  // Auto-fit grid for a row of <StatCard>s — cards fill as many columns as fit
  // (min 200px each) and collapse to a single column on narrow screens.
  statsGrid: {
    display: 'grid',
    // Four across on wide screens; below 1144px the 1/3-width cards get cramped,
    // so drop straight to two per row (avoids an orphaned 4th card at three-up).
    gridTemplateColumns: 'repeat(4, 1fr)',
    rowGap: tokens.spacingVerticalXXL,
    columnGap: tokens.spacingHorizontalXXL,
    '@media (max-width: 1144px)': {
      gridTemplateColumns: '1fr 1fr',
      rowGap: tokens.spacingVerticalL,
      columnGap: tokens.spacingHorizontalL,
    },
  },
});

// Each margin/padding key's letter maps to the Fluent spacing token of the same
// size (S=8, L=16, XXL=24, XXXL=32px).
export const useSpacingStyles = makeStyles({
  mtS: { marginTop: tokens.spacingVerticalS },
  mtL: { marginTop: tokens.spacingVerticalL },
  mtXXL: { marginTop: tokens.spacingVerticalXXL },
  mtXXXL: { marginTop: tokens.spacingVerticalXXXL },
  mbS: { marginBottom: tokens.spacingVerticalS },
  mbL: { marginBottom: tokens.spacingVerticalL },
  mbXXL: { marginBottom: tokens.spacingVerticalXXL },
  mbXXXL: { marginBottom: tokens.spacingVerticalXXXL },
  pS: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
  pL: {
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },
  pXXL: {
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  pXXXL: {
    paddingTop: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    paddingRight: tokens.spacingHorizontalXXXL,
  },
});

export const useTextStyles = makeStyles({
  small: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: 300,
    lineHeight: 1.55,
  },
  muted: { color: brand.gray50 },
  error: { color: brand.error },
  medium: { fontWeight: tokens.fontWeightMedium },
  semibold: { fontWeight: tokens.fontWeightSemibold },
  uppercase: { textTransform: 'uppercase' },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

/**
 * Callout accents. Prefer Fluent MessageBar for true alerts; these remain for
 * inline left-border note styling.
 */
export const useCalloutStyles = makeStyles({
  info: {
    borderLeftWidth: tokens.strokeWidthThicker,
    borderLeftStyle: 'solid',
    borderLeftColor: brand.red,
    paddingLeft: tokens.spacingHorizontalL,
  },
  warning: {
    borderLeftWidth: tokens.strokeWidthThicker,
    borderLeftStyle: 'solid',
    borderLeftColor: brand.wasatchSunrise,
    paddingLeft: tokens.spacingHorizontalS,
  },
  error: {
    borderLeftWidth: tokens.strokeWidthThicker,
    borderLeftStyle: 'solid',
    borderLeftColor: brand.error,
    paddingLeft: tokens.spacingHorizontalS,
  },
  success: {
    borderLeftWidth: tokens.strokeWidthThicker,
    borderLeftStyle: 'solid',
    borderLeftColor: brand.mountainGreen,
    paddingLeft: tokens.spacingHorizontalS,
  },
});
